import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { ClaudeMdEditor } from "./ClaudeMdEditor.js";
/**
 * Per-agent settings dialog. Owns two panes: channel subscriptions (above)
 * and the MCP-server editor (below). Ported from Electron's AgentSettings;
 * drops the Desktop-only MCP health check / tool-listing surfaces since
 * they rely on a local MCP probe.
 */
export function AgentSettingsDialog({ workspaceId, agentId, agentName, onClose }) {
    const ctx = useDataContext();
    const [config, setConfig] = useState({ mcpServers: {} });
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [editText, setEditText] = useState("");
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const textareaRef = useRef(null);
    // Channel subscriptions pane state — loads independently of the MCP config
    // so a slow Hub on one doesn't block the other.
    const [channels, setChannels] = useState(null);
    const [subscribedIds, setSubscribedIds] = useState(null);
    const [subsError, setSubsError] = useState(null);
    const [togglingChannelId, setTogglingChannelId] = useState(null);
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            ctx.hub.listChannels(workspaceId),
            ctx.hub.listAgentSubscriptions(workspaceId, agentId),
        ])
            .then(([cs, subs]) => {
            if (cancelled)
                return;
            setChannels(cs);
            setSubscribedIds(new Set(subs.map((c) => c.id)));
        })
            .catch((e) => {
            if (!cancelled)
                setSubsError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId, agentId]);
    const handleToggleSubscription = async (channelId) => {
        if (togglingChannelId)
            return;
        const current = subscribedIds ?? new Set();
        const isSubscribed = current.has(channelId);
        setTogglingChannelId(channelId);
        setSubsError(null);
        try {
            if (isSubscribed) {
                await ctx.hub.unsubscribeAgent(workspaceId, agentId, channelId);
                setSubscribedIds(new Set([...current].filter((id) => id !== channelId)));
            }
            else {
                await ctx.hub.subscribeAgent(workspaceId, agentId, channelId);
                setSubscribedIds(new Set([...current, channelId]));
            }
        }
        catch (e) {
            setSubsError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setTogglingChannelId(null);
        }
    };
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getMcpJsonConfig("agent", agentId)
            .then((cfg) => {
            if (cancelled)
                return;
            setConfig(cfg);
            setEditText(JSON.stringify(stripDisabled(cfg), null, 2));
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, agentId]);
    const handleEdit = () => {
        setEditText(JSON.stringify(stripDisabled(config), null, 2));
        setEditMode(true);
        setError(null);
        setSaved(false);
        setTimeout(() => textareaRef.current?.focus(), 50);
    };
    const handleSave = async () => {
        try {
            const parsed = JSON.parse(editText);
            if (!parsed.mcpServers || typeof parsed.mcpServers !== "object") {
                setError('Config must have a "mcpServers" object');
                return;
            }
            const disabled = config._disabled ?? [];
            const toSave = { ...parsed, _disabled: disabled };
            await ctx.hub.setMcpJsonConfig("agent", agentId, toSave);
            setConfig(toSave);
            setEditMode(false);
            setError(null);
            setSaved(true);
        }
        catch (e) {
            setError(`Invalid JSON: ${e instanceof Error ? e.message : String(e)}`);
        }
    };
    const handleCancel = () => {
        setEditMode(false);
        setEditText(JSON.stringify(stripDisabled(config), null, 2));
        setError(null);
    };
    const handleToggle = async (serverName) => {
        const disabled = (config._disabled ?? []).slice();
        const isDisabled = disabled.includes(serverName);
        const nextDisabled = isDisabled
            ? disabled.filter((n) => n !== serverName)
            : [...disabled, serverName];
        const nextConfig = { ...config, _disabled: nextDisabled };
        await ctx.hub.setMcpJsonConfig("agent", agentId, nextConfig);
        setConfig(nextConfig);
    };
    const servers = config.mcpServers ?? {};
    const disabledList = config._disabled ?? [];
    const serverEntries = Object.entries(servers);
    return (_jsx("div", { style: backdropStyle, children: _jsxs("div", { style: modalStyle, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { style: headerStyle, children: [_jsxs("div", { children: [_jsxs("div", { style: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }, children: [agentName, " \u2014 Settings"] }), _jsx("div", { style: { fontSize: 11, color: "var(--text-muted)", marginTop: 2 }, children: "Agent-level MCP servers extend the workspace set." })] }), _jsx("button", { type: "button", onClick: onClose, style: closeBtnStyle, "aria-label": "Close", children: "\u00D7" })] }), _jsxs("div", { style: bodyStyle, children: [_jsx(ClaudeMdEditor, { scope: { kind: "agent", workspaceId, agentId, agentName } }), _jsx("div", { style: sectionHeaderStyle, children: _jsx("span", { style: sectionTitleStyle, children: "Channel subscriptions" }) }), _jsx("p", { style: hintStyle, children: "Channels this agent receives messages on. Toggling applies immediately \u2014 the agent's channel bridge re-requests its subscription set on the next tool call." }), subsError && _jsx("div", { style: errorStyle, children: subsError }), channels === null || subscribedIds === null ? (_jsx("div", { style: { color: "var(--text-muted)", fontSize: 12 }, children: "Loading channels\u2026" })) : channels.length === 0 ? (_jsx("div", { style: emptyStyle, children: "No channels in this workspace yet." })) : (_jsx("ul", { style: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }, children: channels.map((channel) => {
                                const checked = subscribedIds.has(channel.id);
                                const busy = togglingChannelId === channel.id;
                                return (_jsx("li", { children: _jsxs("label", { style: {
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10,
                                            padding: "8px 12px",
                                            borderRadius: 6,
                                            background: "var(--bg-inset, #0d0e11)",
                                            border: "1px solid var(--border)",
                                            cursor: busy ? "not-allowed" : "pointer",
                                            opacity: busy ? 0.6 : 1,
                                        }, children: [_jsx("input", { type: "checkbox", checked: checked, disabled: busy, onChange: () => void handleToggleSubscription(channel.id) }), _jsxs("span", { style: { fontSize: 13, color: "var(--text-primary)" }, children: ["#", channel.name] }), channel.visibility === "bullpen" && (_jsx("span", { style: {
                                                    fontSize: 10,
                                                    padding: "2px 6px",
                                                    borderRadius: 3,
                                                    background: "var(--bg-tertiary)",
                                                    color: "var(--text-muted)",
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.5,
                                                }, children: "bullpen" })), channel.description && (_jsx("span", { style: {
                                                    fontSize: 11,
                                                    color: "var(--text-muted)",
                                                    flex: 1,
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }, children: channel.description }))] }) }, channel.id));
                            }) })), _jsx("div", { style: { ...sectionHeaderStyle, marginTop: 8 }, children: _jsx("span", { style: sectionTitleStyle, children: "MCP Servers" }) }), loading ? (_jsx("div", { style: { color: "var(--text-muted)", fontSize: 12 }, children: "Loading\u2026" })) : (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                    }, children: !editMode ? (_jsx("button", { type: "button", onClick: handleEdit, style: secondaryBtn, children: "Edit" })) : (_jsxs("div", { style: { display: "flex", gap: 8 }, children: [_jsx("button", { type: "button", onClick: handleCancel, style: secondaryBtn, children: "Cancel" }), _jsx("button", { type: "button", onClick: handleSave, style: primaryBtn, children: "Save" })] })) }), _jsx("p", { style: hintStyle, children: "Agent-specific MCP servers. Override workspace servers by using the same name. Restart the agent to apply changes." }), error && _jsx("div", { style: errorStyle, children: error }), saved && !editMode && (_jsx("div", { style: successStyle, children: "Config saved. Restart the agent to apply." })), editMode ? (_jsx("textarea", { ref: textareaRef, value: editText, onChange: (e) => setEditText(e.target.value), spellCheck: false, style: textareaStyle, onKeyDown: (e) => {
                                        if (e.key === "Tab") {
                                            e.preventDefault();
                                            const ta = e.currentTarget;
                                            const start = ta.selectionStart;
                                            const end = ta.selectionEnd;
                                            setEditText(editText.substring(0, start) + "  " + editText.substring(end));
                                            setTimeout(() => {
                                                ta.selectionStart = ta.selectionEnd = start + 2;
                                            }, 0);
                                        }
                                        if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                                            e.preventDefault();
                                            void handleSave();
                                        }
                                    } })) : serverEntries.length === 0 ? (_jsx("div", { style: emptyStyle, children: "No MCP servers configured. Click Edit to add servers." })) : (_jsx("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: serverEntries.map(([name, srv]) => (_jsx(ServerRow, { name: name, config: srv, enabled: !disabledList.includes(name), onToggle: () => handleToggle(name) }, name))) }))] }))] })] }) }));
}
function ServerRow({ name, config, enabled, onToggle, }) {
    const isHttp = config.type === "http";
    const command = config.command;
    const args = config.args ?? [];
    const url = config.url;
    const subtitle = isHttp ? url : [command, ...args].filter(Boolean).join(" ");
    return (_jsxs("div", { style: {
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 16px",
            borderRadius: 8,
            background: "var(--bg-inset, #0d0e11)",
            border: "1px solid var(--border)",
            opacity: enabled ? 1 : 0.55,
        }, children: [_jsx("div", { style: {
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "var(--bg-tertiary)",
                    color: enabled ? "var(--accent)" : "var(--text-muted)",
                    fontSize: 15,
                    fontWeight: 700,
                    flexShrink: 0,
                }, children: name.charAt(0).toUpperCase() }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8 }, children: [_jsx("span", { style: { fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }, children: name }), isHttp && (_jsx("span", { style: {
                                    fontSize: 10,
                                    padding: "2px 8px",
                                    borderRadius: 3,
                                    fontWeight: 600,
                                    background: "#2563eb",
                                    color: "#e0e7ff",
                                }, children: "HTTP" }))] }), _jsx("div", { style: {
                            fontSize: 11,
                            color: "var(--text-muted)",
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }, children: subtitle })] }), _jsx("button", { type: "button", onClick: onToggle, "aria-label": enabled ? "Disable" : "Enable", style: {
                    width: 40,
                    height: 22,
                    borderRadius: 11,
                    padding: 2,
                    cursor: "pointer",
                    background: enabled ? "var(--success)" : "var(--bg-tertiary)",
                    border: enabled ? "none" : "1px solid var(--border)",
                    transition: "background-color 0.2s",
                    flexShrink: 0,
                    position: "relative",
                }, children: _jsx("div", { style: {
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: "white",
                        transition: "transform 0.2s",
                        transform: enabled ? "translateX(18px)" : "translateX(0)",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    } }) })] }));
}
function stripDisabled(cfg) {
    const { _disabled: _omit, ...rest } = cfg;
    return rest;
}
const backdropStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    padding: 24,
};
const modalStyle = {
    width: "100%",
    maxWidth: 640,
    maxHeight: "100%",
    background: "var(--bg-primary)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
};
const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    borderBottom: "1px solid var(--border)",
};
const closeBtnStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-muted)",
    fontSize: 22,
    padding: "0 6px",
    lineHeight: 1,
};
const bodyStyle = {
    padding: 20,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
};
const secondaryBtn = {
    fontSize: 12,
    padding: "5px 14px",
    borderRadius: 4,
    background: "var(--bg-tertiary)",
    color: "var(--text-secondary)",
    border: "1px solid var(--border)",
    cursor: "pointer",
};
const primaryBtn = {
    fontSize: 12,
    padding: "5px 14px",
    borderRadius: 4,
    background: "var(--accent)",
    color: "white",
    border: "none",
    cursor: "pointer",
};
const hintStyle = {
    fontSize: 12,
    color: "var(--text-muted)",
    margin: 0,
};
const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
};
const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-primary)",
};
const errorStyle = {
    padding: "8px 12px",
    borderRadius: 4,
    background: "rgba(250, 82, 82, 0.1)",
    border: "1px solid var(--error)",
    color: "var(--error)",
    fontSize: 12,
};
const successStyle = {
    padding: "8px 12px",
    borderRadius: 4,
    background: "rgba(64, 192, 87, 0.1)",
    border: "1px solid var(--success)",
    color: "var(--success)",
    fontSize: 12,
};
const textareaStyle = {
    width: "100%",
    minHeight: 300,
    borderRadius: 6,
    padding: 16,
    fontSize: 13,
    fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
    lineHeight: 1.5,
    outline: "none",
    background: "var(--bg-inset, #0d0e11)",
    border: "1px solid var(--border)",
    color: "var(--text-primary, #e1e1e3)",
    resize: "vertical",
    tabSize: 2,
};
const emptyStyle = {
    padding: 32,
    textAlign: "center",
    borderRadius: 6,
    background: "var(--bg-inset, #0d0e11)",
    border: "1px solid var(--border)",
    color: "var(--text-muted)",
    fontSize: 13,
};
//# sourceMappingURL=AgentSettingsDialog.js.map